import React from 'react';
import {
    Create,
    SimpleForm,
    TextInput,
    required,
    regex,
    email,
    useNotify,
    useRedirect,
} from 'react-admin';
import { Alert, Box } from '@mui/material';
import { FormToolbar } from '../../common/FormToolbar';

/**
 * Registering a school. This writes one row and builds no database.
 *
 * The form says so, in the banner, because the alternative is an operator who
 * fills this in, sees "created", and believes the school can be used. It
 * cannot: nothing routes to a school until its database exists and
 * school_db.status is 'active', which is what the Provision button on the edit
 * screen does.
 *
 * On save we go straight to the edit screen, where that button is.
 */

const codeFormat = regex(
    /^[A-Za-z0-9]{3,20}$/,
    'A code is 3-20 characters, letters and digits only.'
);

export const SchoolCreate = props => {
    const notify = useNotify();
    const redirect = useRedirect();

    const onSuccess = (data: any) => {
        notify('School registered. Now build its database with Provision.', {
            type: 'success',
        });
        redirect('edit', 'school', data.id, data);
    };

    return (
        <Create {...props} title="Register a school" mutationOptions={{ onSuccess }}>
            <SimpleForm toolbar={<FormToolbar {...props} />}>
                <Alert severity="info" sx={{ mb: 2, width: '100%' }}>
                    This registers the school only. Its database is built separately, with the
                    <b> Provision </b> button on the next screen. Until then the school exists on
                    paper and nobody can sign in to it.
                </Alert>

                <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                    <TextInput
                        source="code"
                        label="School code"
                        validate={[required(), codeFormat]}
                        helperText="Short handle, e.g. DPSJPR. Becomes part of the branding subdomain. Cannot be changed later."
                        sx={{ flex: 1 }}
                        parse={(v: string) => (v ? v.toUpperCase() : v)}
                    />
                    <TextInput
                        source="name"
                        label="School name"
                        validate={[required()]}
                        sx={{ flex: 2 }}
                    />
                </Box>

                <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                    <TextInput source="board" label="Board" helperText="CBSE / ICSE / state board" sx={{ flex: 1 }} />
                    <TextInput source="city" label="City" sx={{ flex: 1 }} />
                    <TextInput source="state" label="State" sx={{ flex: 1 }} />
                </Box>

                <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                    <TextInput
                        source="country"
                        label="Country"
                        defaultValue="India"
                        sx={{ flex: 1 }}
                    />
                    <TextInput
                        source="timezone"
                        label="Timezone"
                        defaultValue="Asia/Kolkata"
                        helperText="Used for report cut-offs and scheduled tests"
                        sx={{ flex: 1 }}
                    />
                </Box>

                <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                    <TextInput source="contact_name" label="Contact person" sx={{ flex: 1 }} />
                    <TextInput
                        source="contact_email"
                        label="Contact email"
                        validate={[email()]}
                        sx={{ flex: 1 }}
                    />
                    <TextInput source="contact_phone" label="Contact phone" sx={{ flex: 1 }} />
                </Box>
            </SimpleForm>
        </Create>
    );
};
